import type { SWRMutationConfiguration } from "swr/mutation";
import useSWRMutation from "swr/mutation";
import { AppError } from "@/models/appError";
import { MutationRepository } from "@/repositories/mutationRepository";
import { mutation_contest as RpcMutationContest } from "@/infra/api/rpc/api";

const KEY = "MutationService/UpdateContestEntry";

export const ContestEntryResult = {
  UNSPECIFIED: "UNSPECIFIED",
  SUCCEEDED: "SUCCEEDED",
  FAILED_REQUIRE_LEGAL_AGREEMENT_ACCEPTANCE:
    "FAILED_REQUIRE_LEGAL_AGREEMENT_ACCEPTANCE",
  FAILED_CONTEST_NOT_OPENED: "FAILED_CONTEST_NOT_OPENED",
  FAILED_TOO_MANY_ENTRIES: "FAILED_TOO_MANY_ENTRIES",
} as const;
export type ContestEntryResult =
  (typeof ContestEntryResult)[keyof typeof ContestEntryResult];

export type SWRError = AppError;
type SWRKey = ["MutationService/UpdateContestEntry"] | undefined;
type Arg = {
  contestId: string;
  enteredSeriesIds: string[];
};
type Data = ContestEntryResult;

const sendRequest = async ({ contestId, enteredSeriesIds }: Arg) => {
  const mutationRepository = new MutationRepository();
  return mutationRepository
    .updateContestEntry({
      contestId,
      seriesIds: enteredSeriesIds,
    })
    .then(({ result }) => {
      if (!result) {
        throw new Error("Contest entry update failed");
      }
      switch (result) {
        case RpcMutationContest.UpdateContestEntryResponse.Result.SUCCEEDED:
          return ContestEntryResult.SUCCEEDED;
        case RpcMutationContest.UpdateContestEntryResponse.Result
          .FAILED_REQUIRE_LEGAL_AGREEMENT_ACCEPTANCE:
          return ContestEntryResult.FAILED_REQUIRE_LEGAL_AGREEMENT_ACCEPTANCE;
        case RpcMutationContest.UpdateContestEntryResponse.Result
          .FAILED_CONTEST_NOT_OPENED:
          return ContestEntryResult.FAILED_CONTEST_NOT_OPENED;
        case RpcMutationContest.UpdateContestEntryResponse.Result
          .FAILED_TOO_MANY_ENTRIES:
          return ContestEntryResult.FAILED_TOO_MANY_ENTRIES;
        default:
          return ContestEntryResult.UNSPECIFIED;
      }
    });
};

export const useMutateContestEntry = (
  options?: SWRMutationConfiguration<Data, SWRError, SWRKey, Arg>,
) => {
  return useSWRMutation<Data, SWRError, SWRKey, Arg>(
    [KEY],
    async ([_], { arg }) => sendRequest(arg),
    options,
  );
};
